import { useState, useRef, useEffect, useMemo, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { BankValidationResponse, DirectDebitDetails } from '../CommonTypes';
import { CommonValidation } from '../commonValidation/commonValidation';
import { PaymentApi } from 'api/PaymentApi';
import { TFunction } from 'i18next';

import { setBankDetails as setBankDetailsAction } from 'state/slices/customerSlice';
import { useAppDispatch } from 'state/hooks';
import { AccountType } from 'Constants/Common';

export const accountHolderNameKey = 'accountHolderName';
export const sortCodeKey = 'sortCode';
export const accountNumberKey = 'accountNumber';
export const paymentDayKey = 'paymentDay';

export type BankDetailErrors = {
    [key: string]: string;
};

export function useBankDetailsForm({
    onSuccess,
    t,
    initialAgreements = {},
    requireAgreements = false,
    onBack,
    showPaymentDay = true,
    showBankInputSection,
    accountType,
    referenceNumber,
    businessPartnerId,
    includeConfirmationValidation = false
}: {
    onSuccess: (details: DirectDebitDetails) => void;
    t: TFunction<'translation', undefined>;
    initialAgreements?: {
        agreedDirectDebit?: boolean;
        agreedDirectDebitDueDate?: boolean;
    };
    requireAgreements?: boolean;
    onBack?: () => void;
    showPaymentDay?: boolean;
    showBankInputSection?: boolean;
    accountType: string;
    referenceNumber?: string;
    businessPartnerId: number;
    includeConfirmationValidation?: boolean;
}) {
    const dispatch = useAppDispatch();
    const scrollDivRef = useRef<null | HTMLDivElement>(null);
    const navigate = useNavigate();
    const { getAccessTokenSilently } = useAuth0();
    const [addDisbursement, setAddDisbursement] = useState(false);
    const [bankDetails, setBankDetails] = useState({
        [accountHolderNameKey]: '',
        [sortCodeKey]: Array.from(new Set(['1', '2', '3'])).map((key) => ({
            key,
            value: ''
        })),
        [accountNumberKey]: '',
        [paymentDayKey]: '31',
        accountType: accountType,
        referenceNumber: referenceNumber,
        businessPartnerId: businessPartnerId
    });
    const [bankDetailErrors, setBankDetailErrors] = useState<BankDetailErrors>(
        {}
    );
    const [validateBankAccount, setValidateBankAccount] = useState(false);
    const [updatedDirectDebitDetails, setUpdatedDirectDebitDetails] =
        useState<DirectDebitDetails>();
    const [addDisbursementDetails, setAddDisbursementDetails] =
        useState<DirectDebitDetails>();
    const [agreedDirectDebit, setAgreedDirectDebit] = useState(
        initialAgreements.agreedDirectDebit || false
    );
    const [agreedDirectDebitDueDate, setAgreedDirectDebitDueDate] = useState(
        initialAgreements.agreedDirectDebitDueDate || false
    );
    const updateDirectDebit = {
        businessPartnerId: businessPartnerId,
        bankName: null
    };
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [confirmationError, setConfirmationError] = useState('');

    useEffect(() => {
        scrollDivRef.current?.scrollIntoView();
    }, []);
    const updateDirectDebitSuccess = (data: BankValidationResponse) => {
        if (data.isSuccess && updatedDirectDebitDetails) {
            dispatch(
                setBankDetailsAction({
                    accountHolderName:
                        updatedDirectDebitDetails.accountHolderName,
                    accountNumber: updatedDirectDebitDetails.accountNumber,
                    sortCode: `${updatedDirectDebitDetails.sortCode1}${updatedDirectDebitDetails.sortCode2}${updatedDirectDebitDetails.sortCode3}`,
                    businessPartnerId:
                        updatedDirectDebitDetails.businessPartnerId,
                    bankName: updatedDirectDebitDetails.bankName,
                    accountType: accountType,
                    referenceNumber: referenceNumber
                })
            );
            onSuccess?.(updatedDirectDebitDetails);
        } else {
            updateDirectDebitFailure();
        }
    };
    const updateDirectDebitFailure = () => {
        const newError = {
            ...bankDetailErrors,
            bankValidation: t('direct_debit_guarantee.update-bank-failed')
        };
        setBankDetailErrors(newError);
    };
    const updateDirectDebitBankDetails = async () => {
        const token = await getAccessTokenSilently();
        const controller = new AbortController();
        new PaymentApi().UpdateDirectDebit(
            updatedDirectDebitDetails,
            token,
            updateDirectDebitSuccess,
            updateDirectDebitFailure,
            controller.signal
        );
    };
    //update disbursement with collection bank details
    const updateDisbursementSuccess = () => {
        dispatch(
            setBankDetailsAction({
                accountHolderName: addDisbursementDetails.accountHolderName,
                accountNumber: addDisbursementDetails.accountNumber,
                sortCode: `${addDisbursementDetails.sortCode1}${addDisbursementDetails.sortCode2}${addDisbursementDetails.sortCode3}`,
                businessPartnerId: addDisbursementDetails.businessPartnerId,
                bankName: '',
                accountType: accountType,
                referenceNumber: referenceNumber
            })
        );
        onSuccess?.(addDisbursementDetails);
    };
    const updateDisbursementFailure = (error: string) => {
        const newError = {
            ...bankDetailErrors,
            bankValidation:
                t('direct_debit_guarantee.update-bank-failed') + ' ' + error
        };
        setBankDetailErrors(newError);
    };
    const updateDisbursementBankDetails = async () => {
        const token = await getAccessTokenSilently();
        const controller = new AbortController();

        new PaymentApi().UpdateDirectDebit(
            addDisbursementDetails,
            token,
            updateDisbursementSuccess,
            updateDisbursementFailure,
            controller.signal
        );
    };
    //end update disbursement with collection bank details
    const updateBankDetiails = async (bankName: string) => {
        if (bankName && updatedDirectDebitDetails) {
            setUpdatedDirectDebitDetails({
                ...updatedDirectDebitDetails,
                businessPartnerId: updateDirectDebit.businessPartnerId,
                bankName: bankName,
                accountType: accountType,
                referenceNumber: referenceNumber
            });
            await updateDirectDebitBankDetails();
        }
    };
    const successCallback = async (data: BankValidationResponse) => {
        if (data.isSuccess && validateBankAccount) {
            await updateBankDetiails(data.text || '');
            setValidateBankAccount(false);
        } else {
            setValidateBankAccount(false);
            const newError = {
                ...bankDetailErrors,
                bankValidation: t('validation-failed') + ' ' + data.text
            };
            setBankDetailErrors(newError);
        }
    };
    const failureCallback = (error: string) => {
        setValidateBankAccount(false);
        const newError = {
            ...bankDetailErrors,
            bankValidation: t('validation-failed') + ' ' + error
        };
        setBankDetailErrors(newError);
    };
    const validateDirectDebitBankDetails = async () => {
        const token = await getAccessTokenSilently();
        const controller = new AbortController();

        if (validateBankAccount === true) {
            new PaymentApi().validateDirectDebit(
                updatedDirectDebitDetails,
                token,
                successCallback,
                failureCallback,
                controller.signal
            );
        }
        return () => {
            controller.abort();
        };
    };

    useEffect(() => {
        if (!addDisbursement) {
            validateDirectDebitBankDetails();
        }
    }, [validateBankAccount, addDisbursement]);

    useEffect(() => {
        if (addDisbursement) {
            updateDisbursementBankDetails();
        }
    }, [addDisbursement === true]);

    const onChange = (e: ChangeEvent<HTMLInputElement>, key: string) => {
        if (bankDetails[key] === e.currentTarget.value) {
            return;
        }
        setBankDetails({ ...bankDetails, [key]: e.currentTarget.value });
    };

    const onPaymentDayChange = (paymentDay: string) => {
        setBankDetails({ ...bankDetails, paymentDay });
    };

    const onSortCodeChange = (sortCode: { key: string; value: string }[]) => {
        setBankDetails({ ...bankDetails, sortCode });
    };

    const getErrors = (): string[] => {
        const errors = Object.values(bankDetailErrors).filter(
            (v): v is string => Boolean(v)
        );

        // Only include confirmation error if there are no bank detail errors
        if (errors.length === 0 && confirmationError) {
            errors.push(confirmationError);
        }

        return errors;
    };

    const daysOptions = useMemo(
        () =>
            Array.from({ length: 31 }, (_, i) => {
                const day = i + 1;
                const stringDay = day.toString();
                return {
                    id: stringDay,
                    text: stringDay,
                    value: day
                };
            }),
        []
    );

    const validateBankDetails = () => {
        const validationErrors: BankDetailErrors = {};
        let isValid = true;
        const accountNumberLength = 8;
        if (
            requireAgreements &&
            (!agreedDirectDebit || !agreedDirectDebitDueDate)
        ) {
            validationErrors.directDebitPolicy = t(
                'direct_debit_guarantee.policy_acceptance_required'
            );
        }
        if (!CommonValidation.isEmpty(bankDetails.accountNumber)) {
            if (!CommonValidation.isValidNumeric(bankDetails.accountNumber)) {
                validationErrors.accountNumber = t(
                    'direct_debit_guarantee.account_number_invalid'
                );
                isValid = false;
            } else if (
                bankDetails.accountNumber?.toString().length !==
                accountNumberLength
            ) {
                validationErrors.accountNumber = t(
                    'direct_debit_guarantee.account_number_length',
                    { accountNumberLength }
                );
                isValid = false;
            }
        } else {
            validationErrors.accountNumber = t(
                'direct_debit_guarantee.account_number_required'
            );
            isValid = false;
        }

        setBankDetailErrors(validationErrors);
        return isValid;
    };

    const onSubmit = () => {
        setUpdatedDirectDebitDetails({
            accountHolderName: bankDetails.accountHolderName,
            sortCode1: bankDetails.sortCode[0].value,
            sortCode2: bankDetails.sortCode[1].value,
            sortCode3: bankDetails.sortCode[2].value,
            accountNumber: bankDetails.accountNumber,
            selectedPaymentDay: bankDetails.paymentDay,
            accountType: accountType,
            businessPartnerId: updateDirectDebit.businessPartnerId,
            referenceNumber: referenceNumber
        });

        setConfirmationError('');

        const isValid = validateBankDetails();
        if (isValid) {
            // Only check confirmation if bank details are valid
            if (includeConfirmationValidation && !isConfirmed) {
                setConfirmationError(
                    t('repayment_setup.toggle_confirmation_error')
                );
                return;
            }
            setValidateBankAccount(true);
        }
    };

    const validateConfirmation = () => {
        if (includeConfirmationValidation && !isConfirmed) {
            setConfirmationError(
                t('repayment_setup.toggle_confirmation_error')
            );
            return false;
        }
        setConfirmationError('');
        return true;
    };

    const onYesButtonClick = () => {
        setAddDisbursement(true);
        setAddDisbursementDetails({
            ...addDisbursementDetails,
            businessPartnerId: businessPartnerId,
            bankName: '',
            accountHolderName:
                updatedDirectDebitDetails?.accountHolderName || '',
            sortCode1: updatedDirectDebitDetails?.sortCode1 || '',
            sortCode2: updatedDirectDebitDetails?.sortCode2 || '',
            sortCode3: updatedDirectDebitDetails?.sortCode3 || '',
            accountNumber: updatedDirectDebitDetails?.accountNumber || '',
            selectedPaymentDay:
                updatedDirectDebitDetails?.selectedPaymentDay || '31',
            accountType: AccountType.Disbursement
        } as DirectDebitDetails);
    };

    return {
        scrollDivRef,
        bankDetails,
        setBankDetails,
        bankDetailErrors,
        setBankDetailErrors,
        validateBankAccount,
        setValidateBankAccount,
        updatedDirectDebitDetails,
        setUpdatedDirectDebitDetails,
        agreedDirectDebit,
        setAgreedDirectDebit,
        agreedDirectDebitDueDate,
        setAgreedDirectDebitDueDate,
        isConfirmed,
        setIsConfirmed,
        confirmationError,
        setConfirmationError,
        onChange,
        onPaymentDayChange,
        onSortCodeChange,
        getErrors,
        daysOptions,
        onSubmit,
        onYesButtonClick,
        validateBankDetails,
        validateConfirmation,
        onBack: onBack || (() => navigate(-1)),
        showPaymentDay,
        showBankInputSection
    };
}
